/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-01 20:18:01
 **/

import { useCallback } from "react";
import { useAtomCallback } from "jotai/utils";
import { isEmpty, omit } from "lodash-es";

import { JotaiStore } from "@/frameworks/libraries/commons";
import { Properties } from "@/frameworks/libraries/properties";
import { Configure } from "@/frameworks/libraries/utilities";

import PhoneCallAtoms from "../PhoneCallAtoms";

const LOGTAG = "#useBoothAPI#";

export default function useBoothAPI() {
	const exists = useAtomCallback(
		useCallback((get, _, callID: string) => {
			const room = get(PhoneCallAtoms.Call(callID));
			return !isEmpty(room);
		}, []),
		{ store: JotaiStore }
	);

	const add_or_update_call = useAtomCallback(
		useCallback((get, set, data: PushServices.Call.StatusChanged.Data) => {
			const call = get(PhoneCallAtoms.Call(data.callId));
			set(PhoneCallAtoms.Call(data.callId), {
				...convert_statusdata_to_userinfo(call, data),
				...(isEmpty(call) ? { time: 0 } : {})
			});
			Configure.useLogLevel >= 8 &&
				console.debug(LOGTAG, "add_or_update_call:", get(PhoneCallAtoms.Call(data.callId)));
		}, []),
		{ store: JotaiStore }
	);

	const update_call_time = useAtomCallback(
		useCallback((_, set, callID: string, time: number | ((prev: number) => number)) => {
			set(PhoneCallAtoms.CallTime(callID), time);
		}, []),
		{ store: JotaiStore }
	);

	const update_record_call_time = useAtomCallback(
		useCallback((_, set, callID: string, time: number | ((prev: number) => number)) => {
			set(PhoneCallAtoms.RecordTime(callID), time);
		}, []),
		{ store: JotaiStore }
	);

	const remove_call = useAtomCallback(
		useCallback((_, set, callID: string) => {
			set(PhoneCallAtoms.CallList, (prev) => omit(prev, callID));
		}, []),
		{ store: JotaiStore }
	);

	const convert_statusdata_to_userinfo = (
		call: PushServices.Call.Info,
		data: PushServices.Call.StatusChanged.Data
	) => {
		const callUsers = data.callUsers.reduce<PushServices.Call.UserInfos>((total, current) => {
			const default_media =
				current.userId === Configure.userID
					? (Properties.property.callDeviceState ?? { audio: "on", video: "off" })
					: { audio: "on", video: "off" };
			const _current: PushServices.Call.UserInfo = {
				...current,
				media: {
					// 통화 나간 유저 미디어 초기화 처리
					...(["ACCEPTED", "REQUESTED"].includes(call.callUsers?.[current.userId ?? ""]?.status)
						? (call.callUsers?.[current.userId ?? ""]?.media ?? {
								...(default_media as PushServices.Call.UserMediaInfo),
								screenShare: false,
								isRecording: false
							})
						: {
								...(default_media as PushServices.Call.UserMediaInfo),
								screenShare: false,
								isRecording: false
							})
				}
			};
			total = {
				...total,
				[current.userId ?? ""]: _current
			};
			return total;
		}, {});
		return {
			callUsers,
			...omit(data, "callUsers")
		} as PushServices.Call.Info;
	};

	return {
		exists,
		add_or_update_call,
		update_call_time,
		remove_call,
		update_record_call_time
	};
}
